import { useCallback } from 'react';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { appsFlyerLogger as logger } from '../../utils/AppsFlyer.logger';
import { getAppsFlyerWebSdk, injectAppsFlyerWebSdk } from '../../utils/AppsFlyer.webSdk';

import type { AppsFlyerResult } from '../../utils/AppsFlyer.types';

/**
 * AppsFlyer PBA Web SDK integration.
 * @see {@link https://support.appsflyer.com/hc/en-us/articles/360001610038-PBA-Web-SDK-integration-guide}
 * @see {@link https://evgen.appsflyer.com/?vertical=ecommerce}
 */
export const useAppsFlyer = (): AppsFlyerResult => {
  const isEnabled = useFeatureFlag('permanent-appsflyer-enabled');

  const initializeAppsFlyer = useCallback<AppsFlyerResult['initializeAppsFlyer']>(async () => {
    if (!isEnabled) {
      logger.info('AppsFlyer disabled by feature flag');
      return;
    }

    logger.info('Initializing AppsFlyer');
    injectAppsFlyerWebSdk();
  }, [isEnabled]);

  const setCustomerUserId = useCallback<AppsFlyerResult['setCustomerUserId']>(
    (customerUserId) => {
      if (!isEnabled || !customerUserId) return;

      logger.info(`setCustomerUserId(${customerUserId})`);
      getAppsFlyerWebSdk()?.('pba', 'setCustomerUserId', customerUserId);
    },
    [isEnabled],
  );

  const logCompleteRegistrationEvent = useCallback<
    AppsFlyerResult['logCompleteRegistrationEvent']
  >(() => {
    if (!isEnabled) return;

    logger.info('Logging af_complete_registration event');

    getAppsFlyerWebSdk()?.('pba', 'event', {
      eventType: 'EVENT',
      eventName: 'af_complete_registration',
      eventValue: {
        af_registration_method: 'email',
      },
    });
  }, [isEnabled]);

  const logLoginEvent = useCallback<AppsFlyerResult['logLoginEvent']>(() => {
    if (!isEnabled) return;

    logger.info('Logging af_login event');

    getAppsFlyerWebSdk()?.('pba', 'event', {
      eventType: 'EVENT',
      eventName: 'af_login',
      eventValue: {},
    });
  }, [isEnabled]);

  const logContentViewEvent = useCallback<AppsFlyerResult['logContentViewEvent']>(
    (payload) => {
      if (!isEnabled) return;

      logger.info('Logging af_content_view event', payload);

      getAppsFlyerWebSdk()?.('pba', 'event', {
        eventType: 'EVENT',
        eventName: 'af_content_view',
        eventValue: {
          af_price: payload.price,
          af_content_id: payload.contentId,
          af_content_type: payload.contentType,
        },
      });
    },
    [isEnabled],
  );

  const logAddToCartEvent = useCallback<AppsFlyerResult['logAddToCartEvent']>(
    (payload) => {
      if (!isEnabled) return;

      logger.info('Logging af_add_to_cart event', payload);

      getAppsFlyerWebSdk()?.('pba', 'event', {
        eventType: 'EVENT',
        eventName: 'af_add_to_cart',
        eventValue: {
          af_price: payload.price,
          af_content_id: payload.contentId,
          af_content_type: payload.contentType,
          af_currency: 'USD',
          af_quantity: payload.quantity,
        },
      });
    },
    [isEnabled],
  );

  const logInitiatedCheckoutEvent = useCallback<AppsFlyerResult['logInitiatedCheckoutEvent']>(
    (payload) => {
      if (!isEnabled) return;

      logger.info('Logging af_initiated_checkout event', payload);

      getAppsFlyerWebSdk()?.('pba', 'event', {
        eventType: 'EVENT',
        eventName: 'af_initiated_checkout',
        eventValue: {
          af_price: payload.price,
          af_content_id: payload.productIds,
          af_content_type: payload.orderType,
          af_currency: 'USD',
          af_quantity: payload.quantity,
        },
      });
    },
    [isEnabled],
  );

  const logPurchaseEvent = useCallback<AppsFlyerResult['logPurchaseEvent']>(
    (payload) => {
      if (!isEnabled) return;

      logger.info('Logging af_purchase event', payload);

      getAppsFlyerWebSdk()?.('pba', 'event', {
        eventType: 'EVENT',
        eventName: 'af_purchase',
        eventRevenue: payload.orderValue,
        eventRevenueCurrency: 'USD',
        eventValue: {
          af_order_id: payload.orderId,
          af_quantity: payload.bagCount,
          af_price: payload.orderValue,
          af_content_id: payload.productIds,
          af_content_type: payload.orderType,
          af_currency: 'USD',
        },
      });
    },
    [isEnabled],
  );

  return {
    initializeAppsFlyer,
    setCustomerUserId,
    logCompleteRegistrationEvent,
    logLoginEvent,
    logContentViewEvent,
    logAddToCartEvent,
    logInitiatedCheckoutEvent,
    logPurchaseEvent,
  };
};
